import { useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

function runSnippet(snippet) {
  if (!snippet) return;

  // Create a container so we can safely insert HTML and then re-execute scripts.
  const container = document.createElement('div');
  container.setAttribute('data-chat-widget-container', 'true');
  // Do not hide the container: some providers may render visible DOM nodes inside the snippet.
  // (Most widgets inject their own UI elsewhere, but this keeps us compatible with snippets that include visible markup.)
  container.innerHTML = snippet;
  document.body.appendChild(container);

  // Ensure <script> tags execute by recreating them.
  const scripts = Array.from(container.querySelectorAll('script'));
  for (const oldScript of scripts) {
    const s = document.createElement('script');
    s.setAttribute('data-chat-widget-script', 'true');

    // Copy attributes
    for (const attr of Array.from(oldScript.attributes)) {
      s.setAttribute(attr.name, attr.value);
    }

    if (oldScript.textContent) {
      s.text = oldScript.textContent;
    }

    oldScript.parentNode?.replaceChild(s, oldScript);
  }
}

function cleanupChatWidget() {
  // Remove our injected container + scripts
  document.querySelectorAll('[data-chat-widget-container="true"]').forEach((el) => el.remove());
  document.querySelectorAll('script[data-chat-widget-script="true"]').forEach((el) => el.remove());

  // Best-effort removal for common providers (prevents widget showing on admin/staff pages)
  const selectors = [
    '#tawkchat-container',
    'iframe[title*="chat" i]',
    'iframe[id*="chat" i]',
    'iframe[class*="chat" i]',
    'div[id*="crisp" i]',
    'div[class*="crisp" i]',
    'div[id*="intercom" i]',
    'div[class*="intercom" i]',
    '#intercom-container',
    '#intercom-frame',
    '#fc_frame',
    'div[id*="freshchat" i]',
    'div[class*="freshchat" i]',
    'div[id*="chatwoot" i]',
    'div[class*="chatwoot" i]',
    'div[id*="drift" i]',
    'div[class*="drift" i]',
    'div[id*="zendesk" i]',
    'div[class*="zendesk" i]',
    '#hubspot-messages-iframe-container'
  ];

  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  });
}

export default function ChatWidgetLoader() {
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/chat-widget`);
        if (cancelled) return;

        // Avoid duplicate injections
        const alreadyInjected = document.querySelector('[data-chat-widget-container="true"]');
        if (alreadyInjected) return;

        if (res.data?.enabled && res.data?.snippet) {
          runSnippet(res.data.snippet);
        }
      } catch (e) {
        // Silent fail on public pages
      }
    };

    load();

    return () => {
      cancelled = true;
      cleanupChatWidget();
    };
  }, []);

  return null;
}
